import { Action, Reducer, ActionCreator, combineReducers } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { batch } from 'react-redux';
import { DataManager, Query } from '@syncfusion/ej2-data';
import moment from 'moment';

import { RootState } from '..';
import * as listGridStore from './listGrid.store';
import { IWebinarsListState, IWebinarsListFilterState } from '../../interfaces/states/IWebinarsListState';
import { IWebinar } from '../../common/model/webinar.model';
import { IODataService, } from '../../services';
import * as webinarUtils from '../../utils/webinar.utils';

export enum Type {
  RESET = '@@webinarsList/RESET',
  RESET_HARD = '@@webinarsList/RESET_HARD',
  SET_WEBINARS = '@@webinars/SET_WEBINARS',
  SET_MODE = '@@webinarsList/SET_MODE',
  SET_IS_CHANGING_MODE = '@@webinarsList/SET_IS_CHANGING_MODE',
  SET_FILTER = '@@webinarsList/SET_FILTER',
  SET_FILTER_NAME = '@@webinarsList/SET_FILTER_NAME',
  SET_FILTER_SPEAKERS = '@@webinarsList/SET_FILTER_SPEAKERS',
  SET_FILTER_DATE = '@@webinarsList/SET_FILTER_DATE',
  SET_FILTER_DESCRIPTION = '@@webinarsList/SET_FILTER_DESCRIPTION',
  SET_FILTER_VIDEO_URL = '@@webinarsList/SET_FILTER_VIDEO_URL',
  SET_FILTER_ACTIVE = '@@webinarsList/SET_FILTER_ACTIVE',
  SET_IS_FETCHING = '@@webinarsList/SET_IS_FETCHING',
  SET_SEARCH_DATE = '@@webinarsList/SET_SEARCH_DATE',
  SET_CURRENT_PAGE = '@@webinarsList/SET_CURRENT_PAGE',
  SET_PAGE_SIZE = '@@webinarsList/SET_PAGE_SIZE',
  SET_TOTAL_COUNT = '@@webinarsList/SET_TOTAL_COUNT',
  SET_SORTING = '@@webinarsList/SET_SORTING'
}

const initialState: IWebinarsListState = {
  webinars: [],
  mode: null,
  isChangingMode: true,
  filter: {
    name: null,
    speakers: null,
    date: [],
    description: null,
    videoUrl: null,
    active: null
  },
  grid: listGridStore.initialState
};

// Actions

export type Actions = ResetAction | ResetHardAction | SetWebinarsAction | SetModeAction | SetIsChangingModeAction | SetFilterAction | SetFilterNameAction | SetFilterSpeakersAction | SetFilterDateAction
  | SetFilterDescriptionAction | SetFilterActiveAction | listGridStore.SetIsFetchingAction | listGridStore.SetSearchDateAction | listGridStore.SetCurrentPageAction | listGridStore.SetPageSizeAction
  | listGridStore.SetTotalCountAction | listGridStore.SetSortingAction;

export type ResetAction = Action<Type.RESET>

export type ResetHardAction = Action<Type.RESET_HARD>

export interface SetWebinarsAction extends Action<Type.SET_WEBINARS> {
  webinars: IWebinar[];
}

export interface SetModeAction extends Action<Type.SET_MODE> {
  mode: string;
}

export interface SetIsChangingModeAction extends Action<Type.SET_IS_CHANGING_MODE> {
  isChangingMode: boolean;
}

export interface SetFilterAction extends Action<Type.SET_FILTER> {
  filter: IWebinarsListFilterState;
}

export interface SetFilterNameAction extends Action<Type.SET_FILTER_NAME> {
  name: string;
}

export interface SetFilterSpeakersAction extends Action<Type.SET_FILTER_SPEAKERS> {
  speakers: string;
}

export interface SetFilterDateAction extends Action<Type.SET_FILTER_DATE> {
  date: Date[];
}

export interface SetFilterDescriptionAction extends Action<Type.SET_FILTER_DESCRIPTION> {
  description: string;
}

export interface SetFilterVideoUrlAction extends Action<Type.SET_FILTER_VIDEO_URL> {
  videoUrl: string;
}

export interface SetFilterActiveAction extends Action<Type.SET_FILTER_ACTIVE> {
  active?: boolean;
}

export const reset: ActionCreator<ResetAction> = () => ({
  type: Type.RESET
});

export const resetHard: ActionCreator<ResetHardAction> = () => ({
  type: Type.RESET_HARD
});

export const setWebinars: ActionCreator<SetWebinarsAction> = (webinarsLocal: IWebinar[]) => ({
  type: Type.SET_WEBINARS,
  webinars: webinarsLocal
});

export const setMode: ActionCreator<SetModeAction> = (modeLocal: string) => ({
  type: Type.SET_MODE,
  mode: modeLocal
});

export const setIsChangingMode: ActionCreator<SetIsChangingModeAction> = (isChanginModeLocal: boolean) => ({
  type: Type.SET_IS_CHANGING_MODE,
  isChangingMode: isChanginModeLocal
});

export const setFilter: ActionCreator<SetFilterAction> = (filterLocal: IWebinarsListFilterState) => ({
  type: Type.SET_FILTER,
  filter: filterLocal
});

export const setFilterName: ActionCreator<SetFilterNameAction> = (name: string) => ({
  type: Type.SET_FILTER_NAME,
  name
});

export const setFilterSpeakers: ActionCreator<SetFilterSpeakersAction> = (speakers: string) => ({
  type: Type.SET_FILTER_SPEAKERS,
  speakers
});

export const setFilterDate: ActionCreator<SetFilterDateAction> = (date: Date[]) => ({
  type: Type.SET_FILTER_DATE,
  date
});

export const setFilterDescription: ActionCreator<SetFilterDescriptionAction> = (description: string) => ({
  type: Type.SET_FILTER_DESCRIPTION,
  description
});

export const setFilterVideoUrl: ActionCreator<SetFilterVideoUrlAction> = (videoUrl: string) => ({
  type: Type.SET_FILTER_VIDEO_URL,
  videoUrl
});

export const setFilterActive: ActionCreator<SetFilterActiveAction> = (active?: boolean) => ({
  type: Type.SET_FILTER_ACTIVE,
  active
});

export const fetchWebinars: ActionCreator<ThunkAction<void, RootState, unknown, Actions>> = (
  oDataService: IODataService,
  webinarsDataManager: DataManager,
  webinarsQuery: Query
) => (
    async (dispatch, getState) => {
      dispatch(listGridStore.setIsFetching(Type.SET_IS_FETCHING, true));

      const currentPageLocal = getState().webinarsListStore.grid.currentPage;
      const pageSizeLocal = getState().webinarsListStore.grid.pageSize;
      const sortFieldLocal = getState().webinarsListStore.grid.sortField;
      const sortDirectionLocal = getState().webinarsListStore.grid.sortDirection;

      let query = webinarsQuery.page(currentPageLocal, pageSizeLocal);
      if (sortFieldLocal && sortDirectionLocal) {
        query = sortDirectionLocal === 'Ascending' ? query.sortBy(sortFieldLocal) : query.sortByDesc(sortFieldLocal);
      }
      const webinarsResponse = await oDataService.executeQueryWithCount<IWebinar>(webinarsDataManager, query);
      const webinarsLocal = webinarsResponse.result.map((webinar) => webinarUtils.initDates(webinar));
      batch(() => {
        dispatch(setWebinars(webinarsLocal));
        dispatch(listGridStore.setSearchDate(Type.SET_SEARCH_DATE, moment()));
        dispatch(listGridStore.setTotalCount(Type.SET_TOTAL_COUNT, webinarsResponse.count));
      });
      dispatch(listGridStore.setIsFetching(Type.SET_IS_FETCHING, false));
    }
);

// Reducers

const webinars: Reducer<IWebinar[]> = (state = initialState.webinars, action) => {
  switch (action.type) {
    case Type.RESET:
      return state;
    case Type.SET_MODE:
      return initialState.webinars;
    case Type.SET_WEBINARS:
      return (action as SetWebinarsAction).webinars;
    default:
      return state;
  }
};

const mode: Reducer<string> = (state = initialState.mode, action) => {
  switch (action.type) {
    case Type.RESET:
      return state;
    case Type.RESET_HARD:
      return initialState.mode;
    case Type.SET_MODE:
      return (action as SetModeAction).mode;
    default:
      return state;
  }
};

const isChangingMode: Reducer<boolean> = (state = initialState.isChangingMode, action) => {
  switch (action.type) {
    case Type.RESET:
    case Type.RESET_HARD:
      return initialState.isChangingMode;
    case Type.SET_MODE:
      return false;
    case Type.SET_IS_CHANGING_MODE:
      return (action as SetIsChangingModeAction).isChangingMode;
    default:
      return state;
  }
}

const filter: Reducer<IWebinarsListFilterState> = (state = initialState.filter, action) => {
  switch (action.type) {
    case Type.RESET:
      return state;
    case Type.SET_MODE:
      return { ...state, status: (action as SetModeAction).mode };
    case Type.SET_FILTER:
      return (action as SetFilterAction).filter;
    case Type.SET_FILTER_NAME:
      return { ...state, name: (action as SetFilterNameAction).name };
    case Type.SET_FILTER_SPEAKERS:
      return { ...state, speakers: (action as SetFilterSpeakersAction).speakers };
    case Type.SET_FILTER_DATE:
      return { ...state, date: (action as SetFilterDateAction).date };
    case Type.SET_FILTER_DESCRIPTION:
      return { ...state, description: (action as SetFilterDescriptionAction).description };
    case Type.SET_FILTER_VIDEO_URL:
      return { ...state, videoUrl: (action as SetFilterVideoUrlAction).videoUrl };
    case Type.SET_FILTER_ACTIVE:
      return { ...state, active: (action as SetFilterActiveAction).active };
    default:
      return state;
  }
};

export const webinarsListStore = combineReducers({
  webinars,
  mode,
  isChangingMode,
  filter,
  grid: listGridStore.createListGridStore(Type, 'date', 'Descending')
});
